import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Upload, X, Camera } from "lucide-react";
import PostcodeInput from "@/components/PostcodeInput";

export default function Sell() {
  const nav = useNavigate();
  const { listingId } = useParams();
  const isEdit = Boolean(listingId);
  const [meta, setMeta] = useState({ categories: [], conditions: [] });
  const [form, setForm] = useState({
    title: "", description: "", category: "", condition: "", price: "",
    quantity: 1, location: "", postcode: "", contact_email: "", contact_phone: "",
    is_clearance: false,
  });
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/meta/categories").then(({ data }) => setMeta(data));
  }, []);

  const [loadingListing, setLoadingListing] = useState(isEdit);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/listings/${listingId}`);
        setForm({
          title: data.title || "",
          description: data.description || "",
          category: data.category || "",
          condition: data.condition || "",
          price: data.price ?? "",
          quantity: data.quantity ?? 1,
          location: data.location || "",
          postcode: data.postcode || "",
          contact_email: data.contact_email || "",
          contact_phone: data.contact_phone || "",
          is_clearance: !!data.is_clearance,
        });
        setImages(data.images || []);
      } catch (err) {
        toast.error("Failed to load listing for editing");
        nav("/my-listings");
      } finally {
        setLoadingListing(false);
      }
    })();
  }, [isEdit, listingId]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files.slice(0, 6 - images.length)) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, data.path]);
      } catch (err) {
        toast.error("Upload failed");
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeImage = (p) => setImages((prev) => prev.filter((x) => x !== p));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description || !form.category || !form.condition || !form.price || !form.location) {
      toast.error("Please fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        quantity: parseInt(form.quantity || 1),
        currency: "GBP",
        images,
      };
      const { data } = isEdit
        ? await api.put(`/listings/${listingId}`, payload)
        : await api.post("/listings", payload);
      toast.success(isEdit ? "Listing updated" : "Listing submitted — pending admin review");
      nav(`/listings/${data.listing_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to publish");
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingListing) {
    return (
      <div className="mx-auto max-w-4xl px-6 lg:px-10 py-10">
        <p className="text-muted-foreground">Loading listing...</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-6 lg:px-10 py-10">
      <div className="overline text-safety">{isEdit ? "Edit listing" : "Sell or clear"}</div>
      <h1 className="mt-2 font-display font-black text-4xl lg:text-5xl tracking-tighter text-forest">
        {isEdit ? "Edit listing" : "Post a listing"}
      </h1>
      <p className="mt-3 text-muted-foreground max-w-xl">
        {isEdit ? "Update the details below and save your changes." : "Free to post. Feature it for reach when you're ready."}
      </p>

      <form onSubmit={submit} className="mt-10 border border-border bg-white p-6 lg:p-10 space-y-8">
        <Field label="Title *">
          <Input
            value={form.title}
            onChange={set("title")}
            placeholder="e.g. 12 x Herman Miller Aeron chairs — mint"
            data-testid="sell-title"
            className="rounded-none h-12"
          />
        </Field>

        <Field label="Description *">
          <Textarea
            rows={6}
            value={form.description}
            onChange={set("description")}
            placeholder="Condition details, brand, age, collection info…"
            data-testid="sell-description"
            className="rounded-none"
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <Field label="Category *">
            <Select value={form.category} onValueChange={set("category")}>
              <SelectTrigger className="rounded-none h-12" data-testid="sell-category"><SelectValue placeholder="Pick one" /></SelectTrigger>
              <SelectContent className="bg-white">
                {meta.categories?.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Condition *">
            <Select value={form.condition} onValueChange={set("condition")}>
              <SelectTrigger className="rounded-none h-12" data-testid="sell-condition"><SelectValue placeholder="Pick one" /></SelectTrigger>
              <SelectContent className="bg-white">
                {meta.conditions?.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <Field label="Price (£) *">
            <Input
              type="number" step="0.01" min="0"
              value={form.price} onChange={set("price")}
              data-testid="sell-price"
              className="rounded-none h-12"
            />
          </Field>
          <Field label="Quantity">
            <Input
              type="number" min="1"
              value={form.quantity} onChange={set("quantity")}
              data-testid="sell-quantity"
              className="rounded-none h-12"
            />
          </Field>
          <label className="flex items-center gap-3 pt-8">
            <Checkbox
              checked={form.is_clearance}
              onCheckedChange={(v) => set("is_clearance")(!!v)}
              data-testid="sell-clearance"
            />
            <span className="text-sm">Whole-office clearance</span>
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <Field label="Location *">
            <Input
              value={form.location} onChange={set("location")}
              placeholder="London, Manchester…"
              data-testid="sell-location"
              className="rounded-none h-12"
            />
          </Field>
          <Field label="Postcode">
            <PostcodeInput
              value={form.postcode}
              onChange={(v) => set("postcode")(v)}
              onSelectPlace={(p) => { setForm((f) => ({ ...f, postcode: p.postcode, location: p.admin_district || f.location })); }}
              placeholder="EC2A 4NE"
              className="rounded-none h-12"
              testId="sell-postcode"
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <Field label="Contact email (optional)">
            <Input value={form.contact_email} onChange={set("contact_email")} type="email" data-testid="sell-contact-email" className="rounded-none h-12" />
          </Field>
          <Field label="Contact phone (optional)">
            <Input value={form.contact_phone} onChange={set("contact_phone")} data-testid="sell-contact-phone" className="rounded-none h-12" />
          </Field>
        </div>

        {/* Image uploader */}
        <div>
          <div className="overline text-muted-foreground mb-3">Photos (up to 6)</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {images.map((p) => (
              <div key={p} className="relative aspect-square border border-border overflow-hidden">
                <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeImage(p)}
                  className="absolute top-1 right-1 w-7 h-7 bg-white/90 grid place-items-center hover:bg-safety hover:text-white transition-colors duration-200"
                  data-testid={`sell-remove-image`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            {images.length < 6 && (
              <label className="aspect-square border border-dashed border-border grid place-items-center cursor-pointer hover:border-forest transition-colors duration-200 bg-sage-light">
                <input ref={fileRef} type="file" accept="image/*" multiple onChange={onFiles} className="hidden" data-testid="sell-image-input" />
                <div className="flex flex-col items-center gap-2 text-muted-foreground">
                  {uploading ? <Upload className="w-6 h-6 animate-pulse" /> : <Camera className="w-6 h-6" />}
                  <span className="overline text-xs">{uploading ? "Uploading…" : "Add photo"}</span>
                </div>
              </label>
            )}
          </div>
        </div>

        <div className="pt-6 border-t border-border flex flex-wrap gap-3 items-center justify-between">
          <div className="text-xs text-muted-foreground">Free to post · new listings go through admin review before appearing publicly · takes usually under a few hours</div>
          <Button
            type="submit"
            disabled={submitting}
            data-testid="sell-submit"
            className="rounded-none bg-forest hover:bg-forest-800 text-white h-12 px-10"
          >
            {submitting ? (isEdit ? "Saving…" : "Publishing…") : (isEdit ? "Save changes" : "Submit for review →")}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="overline text-muted-foreground mb-2">{label}</div>
      {children}
    </div>
  );
}
